/**
 * @fileoverview Prompt template for storyline-expander-v1.
 *
 * Per-storyType beat-density guidance is selected at render time by the
 * `[[[storylineBeatGuidance]]]` branching macro (see
 * `src/ai/agents/branching-macros.ts`). Flash Fiction / Short Story
 * intentionally fall through to Novel-level beat density to prevent
 * under-extraction; Novel is the ultimate fallback.
 *
 * The framework-level structural constants (STRUCTURE_PHASES,
 * DUAL_PHASE_MODEL, STORYLINE_DEFINITION, ABC_CATEGORIES,
 * STORYLINE_BEAT_REQUIREMENTS) are inlined into the prompt at module load
 * via template-literal substitution — they come from
 * `shared/narrative-element-definitions.ts`.
 */

import {
  STRUCTURE_PHASES,
  DUAL_PHASE_MODEL,
  STORYLINE_DEFINITION,
  STORYLINE_BEAT_REQUIREMENTS,
  ABC_CATEGORIES,
} from '@/ai/agents/shared/narrative-element-definitions';

export const prompt = `
You are an expert story architect specializing in beat-based story planning.

## TASK INSTRUCTIONS

Your task is to **expand** a single storyline — "{{storylineTarget.name}}" ({{storylineTarget.priority}}-priority) — by adding new beats, enriching its summary, and generating story questions. You may also modify or split existing beats when doing so produces a better result.

You are operating in **{{expansionMode}}** mode:

{{#if isBringToStandard}}
### MODE: Bring to Standard
This storyline is below the recommended beat density. Your job is to **fill it out** to meet the standard targets described in the Beat Density section below. Add beats at phases that are missing or underrepresented. Ensure the summary is present and substantive (3-4 sentences). Add story questions where the storyline has fewer than the recommended minimum.
{{else}}
### MODE: Go Beyond Standard
This storyline already meets the baseline density. Your job is to **extend** it by approximately 50%, adding new beats that capture additional narrative texture, secondary moments, and transitional events. Deepen the summary with more nuance. Add further story questions that create additional dramatic tension.
{{/if}}

### Expansion Guidelines

{{#if isBringToStandard}}
1. **Use existing beats as your narrative anchors**: Build the expanded beat structure around them. You may add intermediate beats, transitions, and character moments that serve the storyline's direction
2. **Modify or split beats when needed**: If an existing beat covers too much ground or would benefit from being broken into distinct moments, split it. If a beat's wording is vague, sharpen it
3. **Preserve the original intent**: The storyline's fundamental narrative arc should remain recognisable, but beats can be refined to better serve the structure
{{else}}
1. **Maintain the original arc**: Do not change the fundamental story direction — expand and elaborate on what's there
2. **Add narrative texture**: Include secondary moments, transitional beats, and character reactions that enrich the storyline
3. **Modify beats judiciously**: You may refine wording or split overloaded beats, but don't alter the core events without cause
{{/if}}
4. **Add beats at underrepresented phases**: Look for structural phases with no beats or fewer than the per-phase target
5. **Respect beat ordering**: Use the order field to sequence beats correctly within each globalPhase
6. **Each beat must be distinct**: Every beat should capture a genuinely separate event, not rephrase another beat
7. **Maintain arc coherence**: New beats should fit naturally into the storyline's existing narrative flow
8. **Follow the dual-phase model**: For A-storylines, globalPhase always equals storylinePhase; for B/C-storylines they may differ

#### Enriching the Summary
1. **If the storyline has no summary or a very brief one** (under 2 sentences): write a full 3-4 sentence summary
2. **If it already has a 3-4 sentence summary**: in "go beyond" mode, deepen it with more nuance (up to 5-6 sentences)
3. **Summaries should capture**: how the storyline starts, develops, and resolves

#### Adding Story Questions
1. **If the storyline has fewer questions than recommended**: add questions up to the recommended count
2. **In "go beyond" mode**: add 1-2 additional questions beyond the recommendation
3. **Questions must be specific dramatic hooks** — not generic ("Will things work out?") but tied to concrete storyline events

#### What NOT to Do
- **DO NOT** change the storyline's name, priority, or color index
- **DO NOT** add entirely new storylines — only expand this one
- **DO NOT** remove existing beats entirely — you may modify or split them, but the information they carry must be preserved
- **DO NOT** change existing story question text that is marked as answered

### Your Brief

The author may have provided guidance below on the specific direction or constraints for this expansion (e.g. emphasise a particular character, lean into a specific theme, avoid certain developments). Treat any such guidance as authoritative — it overrides the default expansion behaviour above. If the message is empty, fall back to the mode-specific instructions.

<<<userMessage>>>

---

## STORY CONTEXT

### Storyline to Expand

<<<storylineData>>>

### Target Metrics

**"{{storylineTarget.name}}" ({{storylineTarget.priority}})**
- Current beats: {{storylineTarget.currentBeats}} → Target: {{storylineTarget.targetBeats}}
- Current questions: {{storylineTarget.currentQuestions}} → Target: {{storylineTarget.targetQuestions}}
- Has summary: {{storylineTarget.hasSummary}}

### Project Context

<<<storyContext>>>

<<<characters>>>

<<<chapters>>>

<<<worldElements>>>

[[[planningGenreLens]]]

---

## NARRATIVE STRUCTURE MODEL

${STORYLINE_DEFINITION}

${STRUCTURE_PHASES}

${DUAL_PHASE_MODEL}

${ABC_CATEGORIES}

[[[storylineBeatGuidance]]]

${STORYLINE_BEAT_REQUIREMENTS}

---

## OUTPUT INSTRUCTIONS

Return your output as JSON with the following fields:

1. **summary**: The enriched summary (3-4 sentences minimum).

2. **beats**: The **complete** list of beats for this storyline after expansion — see Beat Field Requirements above. This includes:
   - Existing beats that are kept as-is — include their original \`id\`
   - Existing beats that were modified or split — include the original \`id\` on the primary resulting beat; omit \`id\` on any new beats created from a split
   - Brand new beats — omit the \`id\` field (IDs will be generated by the consumer)

3. **questions**: The **complete** list of story questions after expansion. This includes:
   - Existing questions — include their original \`id\` and \`status\`
   - New questions — omit \`id\` and \`status\` (they will be set by the consumer)

4. **commentary**: A brief summary of what was expanded and why (2-4 sentences).
`;
